/**
 * Каталог водоснабжения: стояки, коллектор и водорозетки.
 *
 * Всё ставится на стену (`OnlyWall`) и живёт в слое сантехники рядом с унитазом и
 * ванной — это одна специальность. Высоты — типовые для квартирной разводки:
 * водорозетки под смеситель на 550 мм, под унитаз и стиральную машину — низко.
 * Цвет точки — по среде: холодная синяя, горячая красная, парная и коллектор — нейтральные.
 * Id элементов — ключи ссылок из слоя и реестра символов, менять нельзя без миграции.
 */
import { LayerKind } from "@/shared/plan/layerKind";
import { CatalogAttach, type CatalogSection } from "../../types";
import { WaterMedium, WaterPointKind, type WaterCatalogItem } from "./types";

/** Цвета сред на чертеже — единственное место, где они заданы. */
export const WATER_MEDIUM_COLOR: Record<WaterMedium, string> = {
	[WaterMedium.Cold]: "#2563eb",
	[WaterMedium.Hot]: "#dc2626",
};

const BOTH: readonly WaterMedium[] = [WaterMedium.Cold, WaterMedium.Hot];
const NEUTRAL = "#475569";

export const waterSection: CatalogSection<WaterCatalogItem> = {
	kind: LayerKind.Plumbing,
	label: "Водоснабжение",
	canChangeDimensions: false,
	subGroups: ["Стояки", "Коллекторы", "Водорозетки"],
	items: [
		{
			id: "riser-cold",
			kind: WaterPointKind.Riser,
			media: [WaterMedium.Cold],
			subGroup: "Стояки",
			name: "Стояк ХВС",
			description: "Ввод холодной воды в квартиру: кран, фильтр, счётчик. Обычно в санузле.",
			width: 110,
			height: 110,
			depth: 60,
			elevation: 600,
			color: WATER_MEDIUM_COLOR[WaterMedium.Cold],
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "cylinder",
				radius: 55,
				height: 2700,
				color: WATER_MEDIUM_COLOR[WaterMedium.Cold],
			},
		},
		{
			id: "riser-hot",
			kind: WaterPointKind.Riser,
			media: [WaterMedium.Hot],
			subGroup: "Стояки",
			name: "Стояк ГВС",
			description: "Ввод горячей воды в квартиру: кран, фильтр, счётчик. Рядом со стояком ХВС.",
			width: 110,
			height: 110,
			depth: 60,
			elevation: 600,
			color: WATER_MEDIUM_COLOR[WaterMedium.Hot],
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "cylinder",
				radius: 55,
				height: 2700,
				color: WATER_MEDIUM_COLOR[WaterMedium.Hot],
			},
		},
		{
			id: "manifold",
			kind: WaterPointKind.Manifold,
			media: BOTH,
			subGroup: "Коллекторы",
			name: "Коллектор",
			description:
				"Гребёнка ХВС и ГВС: от стояков приходит по одной трубе, к каждой водорозетке уходит своя.",
			width: 300,
			height: 200,
			depth: 80,
			elevation: 500,
			color: NEUTRAL,
			attach: CatalogAttach.OnlyWall,
			view3d: { type: "box", width: 300, height: 200, depth: 80, color: NEUTRAL },
		},
		{
			id: "outlet-cold",
			kind: WaterPointKind.Outlet,
			media: [WaterMedium.Cold],
			subGroup: "Водорозетки",
			name: "Водорозетка ХВС",
			description: "Вывод холодной воды: унитаз, стиральная и посудомоечная машина.",
			width: 60,
			height: 60,
			depth: 40,
			elevation: 250,
			color: WATER_MEDIUM_COLOR[WaterMedium.Cold],
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "cylinder",
				radius: 30,
				height: 40,
				color: WATER_MEDIUM_COLOR[WaterMedium.Cold],
			},
		},
		{
			id: "outlet-hot",
			kind: WaterPointKind.Outlet,
			media: [WaterMedium.Hot],
			subGroup: "Водорозетки",
			name: "Водорозетка ГВС",
			description: "Вывод горячей воды: полотенцесушитель, отдельный кран.",
			width: 60,
			height: 60,
			depth: 40,
			elevation: 550,
			color: WATER_MEDIUM_COLOR[WaterMedium.Hot],
			attach: CatalogAttach.OnlyWall,
			view3d: {
				type: "cylinder",
				radius: 30,
				height: 40,
				color: WATER_MEDIUM_COLOR[WaterMedium.Hot],
			},
		},
		{
			id: "outlet-pair",
			kind: WaterPointKind.Outlet,
			media: BOTH,
			subGroup: "Водорозетки",
			name: "Водорозетка ГВС + ХВС",
			description:
				"Парный вывод под смеситель: умывальник, ванна, душ, мойка. Между осями 150 мм, на 550 мм от пола.",
			width: 210,
			height: 60,
			depth: 40,
			elevation: 550,
			color: NEUTRAL,
			attach: CatalogAttach.OnlyWall,
			view3d: { type: "box", width: 210, height: 60, depth: 40, color: NEUTRAL },
		},
	],
};
